import { AgGridReact } from "ag-grid-react";
import * as React from "react";
import { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ColDef, GetRowIdParams, GridOptions, IsRowSelectable, RowNode } from "ag-grid-community";
import {Box, Chip, CircularProgress, Stack, Tooltip, Typography, useTheme} from "@mui/material";
import {DescriptionRounded, DownloadRounded, SendRounded} from "@mui/icons-material";
import ColouredChip from "../../../component/ColouredChip";
import { formatCurrency, formatDate } from "../../../utils/FormatUtils";
import { ID_ERROR, ID_PROCESSING, ID_PUBLISHED, ID_TO_SEND } from "../constants/RoyaltiesStatuses";
import { COLOR_ERROR, COLOR_PUBLISHED, COLOR_TO_SEND } from "../constants/RoyaltiesColors";
import IconButton from "@mui/material/IconButton";
import { useRoyaltiesPrintoutRepository } from "../../../api/repositories/RoyaltiesPrintoutRepository";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import EmptyState from "../../../../../util/component/EmptyState";
import { NumericSummary } from "../../../component/NumericSummary";
import { useParams } from "react-router-dom";
import FinanceDynamicActions from "../../../component/FinanceDynamicActions";
import { useHasScope } from "../../../../../config/globalConfig";
import { DOWNLOAD_REPORT } from "../../../utils/FinanceScope";
import {downloadPDF} from "./ReportTableUtil";
import {decode} from "../../../utils/Base64Utils";


const ReportPreviewTable = (props: any) => {
    const { t } = useTranslation('finance', { keyPrefix: 'royalties' });
    const { downloadPrintout } = useRoyaltiesPrintoutRepository();
    const { id } = useParams();
    const { getSummaryByReportId } = useRoyaltiesPrintoutRepository();
    const hasScope = useHasScope;
    const theme = useTheme();
    const gridRef = useRef<AgGridReact>(null);

    const columnDefs : ColDef[] = [
        {
            field: 'validation', headerName: '', headerTooltip: t('tables.printout.validation'),
            suppressSizeToFit: true, maxWidth: 65,
            sortable: false,
            checkboxSelection: true, showDisabledCheckboxes: true, headerCheckboxSelection: true,
            lockVisible: true, lockPinned: true
        },
        {
            field: 'msName', headerName: t('tables.printout.dmsName'), headerTooltip: t('tables.printout.dmsName'),
            minWidth: 200,
            unSortIcon: true,
        },
        {
            field: 'ctName', headerName: t('tables.printout.name'), headerTooltip: t('tables.printout.name'),
            minWidth: 200,
            unSortIcon: true,
        },
        {
            field: 'ctType', headerName: t('tables.printout.contractor'), headerTooltip: t('tables.printout.contractor'),
            minWidth: 200,
            unSortIcon: true,
        },
        {
            field: 'totalCommissionable', headerName: t('tables.printout.total-commissionable'), headerTooltip: t('tables.printout.total-commissionable'),
            minWidth: 200,
            unSortIcon: true,
            valueFormatter: params => formatCurrency(params.value, 'EUR', 0.01)
        },
        /*{
            field: 'invoiceAmount', headerName: t('tables.printout.invoice-amount'), headerTooltip: t('tables.printout.invoice-amount'),
            minWidth: 200,
            unSortIcon: true,
            valueFormatter: params => formatCurrency(params.value, 'EUR', 0.01)
        },*/
        {
            field: 'sentDate', headerName: t('tables.printout.sent-date'), headerTooltip: t('tables.printout.sent-date'),
            minWidth: 200,
            unSortIcon: true,
            valueFormatter: params => formatDate(params.value)
        },
        {
            field: 'status', headerName: t('tables.printout.status'), headerTooltip: t('tables.printout.status'),
            minWidth: 150, unSortIcon: true,
            pinned: "right", lockPosition: "right", lockVisible: true, lockPinned: true,
            cellRendererSelector: (params) => {
                const status = params.data.status;
                switch (status) {
                    case ID_TO_SEND:
                        return {
                            component: () => <ColouredChip name={ID_TO_SEND} color={COLOR_TO_SEND}/>
                        };
                    case ID_PUBLISHED:
                        return {
                            component: () => <ColouredChip name={ID_PUBLISHED} color={COLOR_PUBLISHED}/>
                        };
                    case ID_PROCESSING:
                        return {
                            component: () => (<Stack sx={{ alignItems: 'center', height: '100%' }} direction={'row'} spacing={1}>
                                        <Typography fontSize={'small'}>
                                            {t(`chips.${ID_PROCESSING}`)}
                                        </Typography>
                                        <CircularProgress size='15px' disableShrink={true}/>
                                    </Stack>
                            )
                        };
                    case ID_ERROR:
                        return {
                            component: () =>
                                <Tooltip title={params.data.errorReason ? t('tables.printout.' + params.data.errorReason) : " "}>
                                    <div style={{ display: "inline-block" }}>
                                        <ColouredChip name={ID_ERROR} color={COLOR_ERROR}/>
                                    </div>
                                </Tooltip>
                        };
                    default:
                        break;
                }
            }
        },
        {
            field: '_actions', headerName: '',
            suppressSizeToFit: true, minWidth: 100, maxWidth: 110, sortable: false,
            pinned: 'right', lockPosition: 'right', lockVisible: true, lockPinned: true,
            cellRendererSelector: (params) => {
                return {
                    component: () =>
                            <FinanceDynamicActions>
                                {hasScope(DOWNLOAD_REPORT) &&
                                    <>
                                        <IconButton sx={{color: theme.palette.accent.main}}
                                                    disabled={params.data.status === ID_PROCESSING || params.data.status === ID_ERROR}
                                                    onClick={() => {
                                                        downloadPrintout(params.data.id, "html").then((res) => {
                                                            if (res?.result) {
                                                                let pdfWindow = window.open("");
                                                                pdfWindow!.document.write(decode(res.result));
                                                            }
                                                        });
                                                    }}>
                                            <DescriptionRounded/>
                                        </IconButton>
                                        <IconButton sx={{color: theme.palette.accent.main}}
                                                    disabled={params.data.status === ID_PROCESSING || params.data.status === ID_ERROR}
                                                    onClick={() => {
                                                        downloadPrintout(params.data.id, "pdf").then((res) => {
                                                            if (res?.result) {
                                                                downloadPDF(res.result, params.data);
                                                            }
                                                        });
                                                    }}>
                                            <DownloadRounded/>
                                        </IconButton>
                                    </>
                                }
                            </FinanceDynamicActions>
                };
            },
            suppressFiltersToolPanel: true
        },
    ];

    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data['id'],
        defaultColDef: {
            sortable: true,
            resizable: true,
            wrapHeaderText: false,
        },
        noRowsOverlayComponentParams: { title: '', content: '' }
    };

    const isRowSelectable = useMemo<IsRowSelectable>(() => {
        return (rowNode: RowNode) => {
            let status = rowNode.data.status;
            return status === ID_TO_SEND;
        };
    }, []);

    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);

    const toolbar = {
        renderInfo: () => (
                <Stack direction={'row'} spacing={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '1ch' }}>
                        <Typography variant={'body1'}>
                            {t('preview.top-label.key')}
                        </Typography>
                        <Typography variant={'body1'} fontWeight={'bold'}>
                            {t('preview.top-label.value', { creationDate: props?.date })}
                        </Typography>
                    </Box>
                    <NumericSummary query={() => getSummaryByReportId(id!)} fields={[ID_PROCESSING, ID_TO_SEND, ID_PUBLISHED, ID_ERROR]} i18nPath={"royalties.counters.printouts"}/>
                </Stack>
        ),
        renderActions: () => (
                <Stack>
                    <Tooltip title={<div style={{ whiteSpace: 'pre-line' }}>{t('preview.buttons.publish.title') + '\n' + t('preview.buttons.publish.tooltip', {
                        selectedRows: props.selectedDataLength
                    })}</div>} arrow>
                        <IconButton disabled={props.data.length === 0 || props.selectedDataLength === 0}
                                    color={'accent'}
                                    onClick={props.onPublishClick}>
                            <SendRounded/>
                        </IconButton>
                    </Tooltip>
                </Stack>
        )
    }

    return (
            <AgGridCustomTable
                    toolbar={toolbar}
                    noRowsOverlayComponent={noRowsOverlayComponent}
                    gridRef={gridRef} columnDefs={columnDefs} gridOptions={gridOptions}
                    rowData={props.data} rowBuffer={0}
                    rowSelection={'multiple'} isRowSelectable={isRowSelectable}
                    suppressRowClickSelection
                    onGridReady={() => {
                        gridRef.current!.api.sizeColumnsToFit();
                        gridRef.current!.api.hideOverlay();
                    }}
                    onFirstDataRendered={() => {
                        gridRef.current!.api.selectAll();
                    }}
                    onRowSelected={(event: any) => {
                        props.onRowSelected(event.node.data.id, event.node.isSelected(), event.node.data);
                    }}
                    onSelectionChanged={() => {
                        props.setSelectedDataLength(gridRef.current!.api.getSelectedRows().length);
                    }}
            />
    );
}

export default ReportPreviewTable;